import { ILayerDefinition } from '@esri/arcgis-rest-feature-service';
import {
  ArcGISIdentityManager,
  IFeature,
  IField,
} from '@esri/arcgis-rest-request';
import {
  CalciteButton,
  CalciteInputMessage,
  CalciteLabel,
} from '@esri/calcite-components-react';
import React, { useMemo } from 'react';
import { Control, useController, useForm } from 'react-hook-form';

import { EsriTypeEditor } from '../GridComponents/Editors/EsriTypeEditor';

export type AddFeaturePanelProps = {
  layerDef: ILayerDefinition;
  session: ArcGISIdentityManager;
  onAddFeature: (feature: IFeature) => void;
};

type EsriTypeEditorFormProps = {
  field: IField;
  control: Control<Record<string, string>, any>;
};

const EsriTypeEditorForm = ({ field, control }: EsriTypeEditorFormProps) => {
  const {
    field: formField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: {
      required: {
        value: !field.nullable,
        message: 'Field is required',
      },
      maxLength: length,
    },
    defaultValue: field.defaultValue,
  });
  return (
    <div>
      <CalciteLabel key={field.name} layout="inline-space-between">
        <span className="flex flex-col w-32 overflow-hidden">
          <span className="whitespace-nowrap overflow-hidden overflow-ellipsis">
            {field.name}
          </span>
          <span className="whitespace-nowrap text-color-3 text-n2">
            {field.alias}
          </span>
        </span>
        {!field.nullable && <span className="text-red-500">*</span>}

        <div className="h-8 w-full">
          <EsriTypeEditor
            type={field.type}
            value={formField.value}
            onChange={(value) => formField.onChange(value)}
            disabled={!field.editable}
            domain={field.domain}
          />
        </div>
      </CalciteLabel>
      <CalciteInputMessage status="valid">{error?.message}</CalciteInputMessage>
    </div>
  );
};

export const AddFeaturePanel: React.FC<AddFeaturePanelProps> = ({
  layerDef,
  onAddFeature,
}) => {
  const { handleSubmit, control } = useForm<Record<string, string>>();

  const onSubmit = handleSubmit((data) => {
    onAddFeature({ attributes: data });
  });

  const editableFields = useMemo(
    () => layerDef.fields?.filter((field) => field.editable),
    [layerDef.fields]
  );

  return (
    <div className="p-2">
      {editableFields?.map((field) => (
        <EsriTypeEditorForm key={field.name} field={field} control={control} />
      ))}

      <CalciteButton iconStart="plus" onClick={onSubmit}>
        Add Row
      </CalciteButton>
    </div>
  );
};
